import { memo } from "react";
import styled from "styled-components";
import { Button, Switch, Tooltip } from "antd";
import { ReloadOutlined, SettingOutlined } from "@ant-design/icons";

interface LayerRowProps {
  icon: React.ReactNode;
  title: string;
  isVisible: boolean;
  onToggle: (checked: boolean) => void;
  onRefresh?: () => void;
  onSettings?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  children?: React.ReactNode;
}

const LayerRow = ({
  icon,
  title,
  isVisible,
  onToggle,
  onRefresh,
  onSettings,
  disabled = false,
  disabledReason,
  children,
}: LayerRowProps) => {
  return (
    <Wrapper>
      <RowContainer $disabled={disabled}>
        <RowLeft>
          <IconWrap>{icon}</IconWrap>
          <RowTitle $disabled={disabled}>{title}</RowTitle>
        </RowLeft>

        <RowActions>
          {onRefresh && (
            <Tooltip title={disabledReason && disabled ? disabledReason : "Làm mới dữ liệu"}>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={onRefresh}
                disabled={disabled || !isVisible}
              />
            </Tooltip>
          )}

          {onSettings && (
            <Tooltip title={disabledReason && disabled ? disabledReason : "Cài đặt hiển thị"}>
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                onClick={onSettings}
                disabled={disabled}
              />
            </Tooltip>
          )}

          <Tooltip title={disabledReason && disabled ? disabledReason : undefined}>
            <Switch
              checked={isVisible}
              onChange={onToggle}
              disabled={disabled}
              size="small"
            />
          </Tooltip>
        </RowActions>
      </RowContainer>

      {children && isVisible && (
        <ChildrenContainer>{children}</ChildrenContainer>
      )}
    </Wrapper>
  );
};

export default memo(LayerRow);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const RowContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  transition: opacity 0.2s;
`;

const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const IconWrap = styled.span`
  font-size: 15px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #64748b;
`;

const RowTitle = styled.span<{ $disabled?: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $disabled }) => ($disabled ? "#94a3b8" : "#374151")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ChildrenContainer = styled.div`
  margin-top: 4px;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
